import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SECTOR_COUNT = 15
const SECTOR_DEG = 360 / SECTOR_COUNT
const WHEEL_START_ANGLE = -90

const getSectorColor = (sector) => {
  if (sector === 0) return 'green'
  return sector % 2 === 0 ? 'red' : 'black'
}

const getSectorRotation = (sector) => {
  const normalized = Number.isFinite(Number(sector)) ? Number(sector) : 0
  const centerAngle = WHEEL_START_ANGLE + normalized * SECTOR_DEG + SECTOR_DEG / 2
  return -centerAngle
}

const buildGradient = () => {
  const colorMap = {
    red: '#ef4444',
    black: '#111827',
    green: '#22c55e'
  }

  const stops = []
  for (let i = 0; i < SECTOR_COUNT; i += 1) {
    const color = colorMap[getSectorColor(i)]
    const start = i * SECTOR_DEG
    const end = start + SECTOR_DEG
    stops.push(`${color} ${start}deg ${end}deg`)
  }

  return `conic-gradient(from ${WHEEL_START_ANGLE}deg, ${stops.join(', ')})`
}

const formatSeconds = (secondsValue) => {
  const seconds = Math.max(0, Number(secondsValue || 0))
  const min = String(Math.floor(seconds / 60)).padStart(2, '0')
  const sec = String(seconds % 60).padStart(2, '0')
  return `${min}:${sec}`
}

const DoubleWheel = ({ roundId, status, prevSector, winSector, winColor, countdownSeconds }) => {
  const { t } = useTranslation()
  const [rotation, setRotation] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const animatedRoundRef = useRef(null)

  const sectorGradient = useMemo(() => buildGradient(), [])
  const displaySector = Number.isFinite(Number(winSector))
    ? Number(winSector)
    : Number.isFinite(Number(prevSector))
      ? Number(prevSector)
      : 0

  const statusLabel = useMemo(() => {
    const statusMap = {
      new: t('double.status.new', 'Делайте ставки'),
      started: t('double.status.started', 'Ставки открыты'),
      raffle: t('double.status.raffle', 'Розыгрыш'),
      result: t('double.status.result', 'Результат'),
      finished: t('double.status.finished', 'Делайте ставки')
    }

    return statusMap[status] || status || t('double.status.unknown', 'Неизвестно')
  }, [status, t])

  const isResultStatus = status === 'result'
  const isStartedStatus = status === 'started'

  const centerText = useMemo(() => {
    if (isResultStatus) {
      return displaySector
    }

    if (isStartedStatus) {
      return formatSeconds(countdownSeconds)
    }

    return statusLabel
  }, [countdownSeconds, displaySector, isResultStatus, isStartedStatus, statusLabel])

  const centerStyle = useMemo(() => {
    if (!isResultStatus) {
      return { backgroundColor: 'rgba(10, 10, 15, 0.95)', color: '#ffffff' }
    }

    const normalizedColor = String(winColor || getSectorColor(displaySector)).toLowerCase()

    if (normalizedColor === 'red') {
      return { backgroundColor: '#ef4444', color: '#ffffff' }
    }

    if (normalizedColor === 'black') {
      return { backgroundColor: '#111827', color: '#ffffff' }
    }

    return { backgroundColor: '#22c55e', color: '#052e16' }
  }, [displaySector, isResultStatus, winColor])

  useEffect(() => {
    if (!roundId) return
    animatedRoundRef.current = null
    setIsAnimating(false)
    setRotation(getSectorRotation(prevSector))
  }, [roundId, prevSector])

  useEffect(() => {
    const canAnimate = ['raffle', 'result', 'finished'].includes(status)
    if (!canAnimate) return
    if (!Number.isFinite(Number(winSector))) return
    if (animatedRoundRef.current === roundId) return

    animatedRoundRef.current = roundId
    setIsAnimating(true)

    const targetRotation = getSectorRotation(winSector) - 360 * 6
    requestAnimationFrame(() => {
      setRotation(targetRotation)
    })
  }, [roundId, status, winSector])

  const labels = Array.from({ length: SECTOR_COUNT }, (_, sector) => {
    const centerConicAngle = WHEEL_START_ANGLE + sector * SECTOR_DEG + SECTOR_DEG / 2
    const angle = centerConicAngle - 90
    const radius = 39
    const x = 50 + radius * Math.cos((angle * Math.PI) / 180)
    const y = 50 + radius * Math.sin((angle * Math.PI) / 180)
    const radialAngle = (Math.atan2(y - 50, x - 50) * 180) / Math.PI

    return {
      sector,
      x,
      y,
      rotation: radialAngle + 90,
      color: getSectorColor(sector)
    }
  })

  return (
    <div className="p-4 lg:p-6">

      <div className="relative mx-auto aspect-square w-full max-w-[340px]">
        <div className="absolute left-1/2 top-1 z-20 -translate-x-1/2">
          <div className="h-0 w-0 border-l-[12px] border-r-[12px] border-b-[20px] border-l-transparent border-r-transparent border-b-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" />
        </div>

        <div
          className="absolute inset-0 rounded-full border-4 border-black/100 shadow-[0_0_40px_rgba(0,0,0,0.35)]"
          style={{
            background: sectorGradient,
            transform: `rotate(${rotation}deg)`,
            transition: isAnimating ? 'transform 10000ms cubic-bezier(0.08,0.88,0.2,1)' : 'none'
          }}
          onTransitionEnd={() => setIsAnimating(false)}
        >
          <svg viewBox="0 0 100 100" className="h-full w-full">
            {labels.map((label) => (
              <text
                key={label.sector}
                x={label.x}
                y={label.y}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${label.rotation} ${label.x} ${label.y})`}
                fill={label.color === 'green' ? '#d1fae5' : '#ffffff'}
                fontSize="6"
                fontWeight="700"
              >
                {label.sector}
              </text>
            ))}
          </svg>
        </div>

        <div
          className="absolute inset-[25%] z-10 rounded-full border border-white/20 flex items-center justify-center transition-colors duration-300"
          style={centerStyle}
        >
          <span className={`text-center leading-tight font-bold px-1.5 ${isStartedStatus ? 'font-mono text-xl' : 'text-sm'}`}>
            {centerText}
          </span>
        </div>
      </div>
    </div>
  )
}

export default DoubleWheel
